import type { OutputCheck } from '@axilon-apps/domains-ontology-ui-state'
import { Card } from '../../../components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table'
import { cn } from '../../../lib/utils'
import { WriteButton } from '../../IngestPage/shared/WriteButton'
import { COMPACT_ACTION, COMPACT_TABLE, MONO_WRAP } from '../../IngestPage/shared/compact'
import { changedSections } from '../util/draftChanges'
import type { ConfigSubject } from '../util/subject'
import { ChangeNote } from './ChangeNote'

export function ChecksTab({
  subject,
  checks,
  edited,
  canManage,
  editLabel,
  onEdit
}: {
  subject: ConfigSubject
  /** The checks to show — the reviewer's overrides on a draft, else the subject's. */
  checks: OutputCheck[]
  /** The reviewer changed them on this draft and they are not saved yet. */
  edited?: boolean
  canManage: boolean
  /** Absent when this subject cannot be edited (an older or archived version). */
  editLabel?: string
  onEdit?: () => void
}) {
  const changed = changedSections(subject.diff).has('checks')
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {subject.compareLabel ? (
          <ChangeNote section="Checks" changed={changed} compareLabel={subject.compareLabel} />
        ) : (
          <span className="text-11 text-ink-3">
            {checks.length} check{checks.length === 1 ? '' : 's'} · each is one SELECT over the
            produced table aliased <code>t</code>
          </span>
        )}
        {editLabel && onEdit ? (
          <WriteButton
            canManage={canManage}
            size="sm"
            variant="outline"
            className={COMPACT_ACTION}
            onClick={onEdit}
          >
            {editLabel}
          </WriteButton>
        ) : null}
      </div>
      {edited ? (
        <p className="text-11 text-st-warn" data-testid="checks-edited-on-draft">
          Edited on this draft — saved with <em>Save as vN</em>.
        </p>
      ) : null}
      <Card className="min-w-0 overflow-hidden p-0">
        {checks.length ? (
          <Table className={COMPACT_TABLE}>
            <TableHeader>
              <TableRow>
                <TableHead>Table</TableHead>
                <TableHead className="w-1/3">Message</TableHead>
                <TableHead>SQL</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {checks.map((check, index) => (
                <TableRow key={`${check.table}-${index}`}>
                  <TableCell className={MONO_WRAP}>{check.table}</TableCell>
                  <TableCell className="text-ink-2">{check.message}</TableCell>
                  <TableCell className={cn(MONO_WRAP, 'text-11 whitespace-pre-wrap')}>
                    {check.sql}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="p-3 text-xs text-ink-3">No checks. Every produced row is written as-is.</p>
        )}
      </Card>
    </div>
  )
}
